import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import { getConversationTitle } from 'contextProviders/InsightsChatProvider/conversationTypes';

const HistoryList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 4px;
`;

const HistoryEmpty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--color-text-secondary);
`;

const HistoryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid var(--color-border-secondary);
  background-color: var(--color-bg-secondary);

  &:hover {
    background-color: var(--color-bg-tertiary);
  }
`;

const HistoryItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const HistoryItemTitle = styled.div`
  color: var(--color-text-primary);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HistoryItemTime = styled.div`
  color: var(--color-text-secondary);
  font-size: 12px;
`;

const TitleInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  font-size: 13px;
  color: var(--color-text-primary);
  background-color: var(--color-bg-primary);
  border: 1px solid var(--color-ai-accent);
  border-radius: 4px;
  padding: 1px 6px;

  &:focus {
    outline: none;
    box-shadow: 0px 0px 0px 2px var(--color-ai-accent-subtle);
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
`;

// The pencil only appears on row hover; delete stays visible.
const EditButton = styled(IconButton)`
  opacity: 0;
  transition: opacity 0.12s ease;

  ${HistoryItem}:hover & {
    opacity: 1;
  }

  &:focus-visible {
    opacity: 1;
  }
`;

// The saved-conversation history list, driven entirely by InsightsChatContext.
// Shared by the slideout (behind its History toggle) and the full-page chat
// (persistent left column). `onSelect` lets the slideout close the history
// panel after a pick; the page passes nothing.
const InsightsConversationList = ({ onSelect }: { onSelect?: () => void }) => {
  const {
    conversations,
    loadConversation,
    deleteConversation,
    renameConversation,
  } = useContext(InsightsChatContext);
  // The conversation whose title is being edited inline (null = none).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>('');

  const commitEdit = () => {
    if (editingId !== null) {
      renameConversation(editingId, draft);
    }
    setEditingId(null);
  };

  if (conversations.length === 0) {
    return (
      <HistoryEmpty>
        <Typography.Body type="Body 12">No past conversations</Typography.Body>
      </HistoryEmpty>
    );
  }

  return (
    <HistoryList data-testid="insights-history-list">
      {conversations.map((conversation) => {
        const title = getConversationTitle(conversation);
        const isEditing = editingId === conversation.id;
        return (
          <HistoryItem
            key={conversation.id}
            data-testid="insights-history-item"
            onClick={() => {
              if (isEditing) {
                return;
              }
              loadConversation(conversation.id);
              onSelect?.();
            }}
          >
            <HistoryItemText>
              {isEditing ? (
                <TitleInput
                  autoFocus
                  value={draft}
                  placeholder={title}
                  aria-label="Conversation title"
                  data-testid="insights-history-title-input"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      commitEdit();
                    } else if (e.key === 'Escape') {
                      setEditingId(null);
                    }
                  }}
                />
              ) : (
                <HistoryItemTitle title={title}>{title}</HistoryItemTitle>
              )}
              <HistoryItemTime>
                {moment(conversation.updatedOn).fromNow()}
              </HistoryItemTime>
            </HistoryItemText>
            {!isEditing && (
              <>
                <EditButton
                  type="button"
                  aria-label="Rename conversation"
                  data-testid="insights-history-rename"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDraft(conversation.customTitle ?? '');
                    setEditingId(conversation.id);
                  }}
                >
                  <Icon
                    icon="edit-outline"
                    width={14}
                    height={14}
                    color="var(--color-text-secondary)"
                  />
                </EditButton>
                <IconButton
                  type="button"
                  aria-label="Delete conversation"
                  data-testid="insights-history-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(conversation.id);
                  }}
                >
                  <Icon
                    icon="cross"
                    width={14}
                    height={14}
                    color="var(--color-text-secondary)"
                  />
                </IconButton>
              </>
            )}
          </HistoryItem>
        );
      })}
    </HistoryList>
  );
};

export default InsightsConversationList;
